import { IPromise } from 'angular';
import { call, put, select } from 'redux-saga/effects';
import * as _ from 'underscore';

import { Messages } from '../../../../codegen/Localize';
import { DatasourceDefinition, WorkbookDefinition } from '../../../../ts/models/ContentDefinitions';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IDataSourceBase } from '../../../../ts/models/IDataSourceInfo';
import {
  IWorkbookBase,
} from '../../../../ts/models/IWorkbookInfo';
import { Set } from '../../../../ts/models/Set';
import { IResult } from '../../../../ts/ServerApi/types/IResponse';
import {
  CreateExtractTasksAction,
  CreateExtractTasksMode
} from '../../../../ts/services/CreateExtractTasksAction';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { CreateExtractRefreshAction } from '../../../actions/spaces/CreateExtractRefreshAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { selectSpaceInfo } from '../../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { SpaceInfo } from '../../../state/SpaceInfo';
import { getDatasourceInfos, getWorkbookInfos } from '../SpacesSagaHelpers';

export function openWorkbookRefreshExtractDialog(
  workbooks: Set<IWorkbookBase>,
  mode: CreateExtractTasksMode
): IPromise<IResult> {
  const createExtractModule = ServiceLocator.get<CreateExtractTasksAction>('CreateExtractTasksAction');
  const context = ServiceLocator.getAngularActionContext();
  return createExtractModule.forWorkbooks(workbooks, context, mode);
}

export function openDatasourceRefreshExtractDialog(
  datasources: Set<IDataSourceBase>,
  mode: CreateExtractTasksMode
): IPromise<IResult> {
  const createExtractModule = ServiceLocator.get<CreateExtractTasksAction>('CreateExtractTasksAction');
  const context = ServiceLocator.getAngularActionContext();
  return createExtractModule.forDatasources(datasources, context, mode);
}

export function openContentRefreshExtractDialog(
  content: Set<IContentInfo>,
  mode: CreateExtractTasksMode
): IPromise<IResult> {
  const createExtractModule = ServiceLocator.get<CreateExtractTasksAction>('CreateExtractTasksAction');
  const context = ServiceLocator.getAngularActionContext();
  return createExtractModule.forContent(content, context, mode);
}

export function* handleCreateExtractRefreshAction(action: CreateExtractRefreshAction) {
  const localizedErrorMessage = Messages.unexpectedError();
  try {
    const contentDictionaryByType = _.groupBy(action.selectedContent, c => c.contentType);
    const typeArray = Object.keys(contentDictionaryByType);

    if (typeArray.length === 1) {
      const singleContentType = typeArray[0];
      switch (singleContentType) {
        case WorkbookDefinition.contentType:
          const workbookInfos = yield call(getWorkbookInfos, contentDictionaryByType[singleContentType]);
          const workbookSet = Set.fromIdItemArray(workbookInfos);
          yield call(openWorkbookRefreshExtractDialog, workbookSet, action.mode);
          break;
        case DatasourceDefinition.contentType:
          const datasourceInfos = yield call(getDatasourceInfos, contentDictionaryByType[singleContentType]);
          const datasourceSet = Set.fromIdItemArray(datasourceInfos);
          yield call(openDatasourceRefreshExtractDialog, datasourceSet, action.mode);
          break;
        default:
          yield put(requestFailed(`${singleContentType} does not support extract refresh.`));
          break;
      }
    } else if (typeArray.length > 1) {
      const workbookInfos = yield call(getWorkbookInfos, contentDictionaryByType[WorkbookDefinition.contentType]);
      const datasourceInfos = yield call(getDatasourceInfos, contentDictionaryByType[DatasourceDefinition.contentType]);
      const contentIds = Set.fromIdItemArray(workbookInfos.concat(datasourceInfos));
      yield call(openContentRefreshExtractDialog, contentIds, action.mode);
    }

    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    if (spaceInfo.subspaceName === 'extractTasks' || spaceInfo.subspaceName === 'extractRefreshes') {
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, localizedErrorMessage));
  }
}
